import React, { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';

import CustomModal from '../../../components/Modals/Modal';
import Link from '../../../components/Link';
import { ModalCloseBtn } from '../../../components/statelessComponents/Buttons/ButtonsInModals';
import BookIcon from '../../../components/statelessComponents/Icons/BookIcon';
import s from './AttachedCoursesModal.scss';
import { buildQuery, isTrial } from '../../../helpers';
import RestrictedAccessModal from '../../../components/RestrictedAccessModal/RestrictedAccessModal';
import isLoggedIn from '../../../helpers/checkAuth';
import ContextProvider from '../../../context';

const messages = defineMessages({
  title: {
    id: 'LessonDetails.AttachedCoursesModal.title',
    defaultMessage: 'Are you interested in this topic? - See courses',
  },
  noCoursesText: {
    id: 'LessonDetails.AttachedCoursesModal.noCoursesText',
    defaultMessage: 'This lesson isn’t currently included in any course.',
  },
  fetchedAttachedCoursesError: {
    id: 'LessonDetails.AttachedCoursesModal.fetchedAttachedCoursesError',
    defaultMessage: 'Oops, something went wrong!',
  },
});

const AttachedCoursesModal = props => {
  const { onClose, numberOfAttachedCourses, lessonId, auth } = props;
  const { fetch } = useContext(ContextProvider);
  const [isAttachedCoursesLoading, setIsAttachedCoursesLoading] = useState(
    false,
  );
  const [attachedCoursesList, setAttachedCoursesList] = useState([]);
  const [
    isRestrictedAccessModalOpened,
    setIsRestrictedAccessModalOpened,
  ] = useState(false);
  const [
    isAttachedCoursesLoadingError,
    setIsAttachedCoursesLoadingError,
  ] = useState(false);

  const fetchAttachedCourses = useCallback(async () => {
    if (!numberOfAttachedCourses || !lessonId) return;

    setIsAttachedCoursesLoading(true);
    try {
      const params = {
        useInLessonId: lessonId,
      };

      const query = buildQuery(params);
      const rawRes = await fetch(`/api/courses/used-in-lesson?${query}`);

      const resText = await rawRes.text();

      const json = resText ? JSON.parse(resText) : [];
      if (rawRes.status >= 400) {
        throw new Error(rawRes.statusText);
      }

      setAttachedCoursesList(json);
    } catch (error) {
      console.error('Error fetching attached courses:', error);
      setIsAttachedCoursesLoadingError(true);
    } finally {
      setIsAttachedCoursesLoading(false);
    }
  }, [
    numberOfAttachedCourses,
    lessonId,
    setIsAttachedCoursesLoading,
    setAttachedCoursesList,
  ]);

  useEffect(() => {
    fetchAttachedCourses();
  }, [fetchAttachedCourses]);

  const hasAccess = course => isLoggedIn(auth) && course.isAvailable;

  const checkAuth = (event, course) => {
    if (hasAccess(course) && !isTrial(auth)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setIsRestrictedAccessModalOpened(true);
  };

  const closeRestrictedAccessModal = () => {
    setIsRestrictedAccessModalOpened(false);
  };

  return (
    <>
      <CustomModal
        isOpen
        width="60%"
        isLoaderOn={isAttachedCoursesLoading}
        data-test="attached-courses-modal"
        aria-describedby="attached-courses-modal"
      >
        {numberOfAttachedCourses > 0 && !isAttachedCoursesLoadingError && (
          <>
            <Typography variant="h6">
              <FormattedMessage {...messages.title} />
            </Typography>
            <List component="nav" className={s.coursesList}>
              {attachedCoursesList?.map(course => (
                <Link
                  data-test-id="PERtH2uPzb5OeqlATmNa8"
                  to={`/courses/${course.slug}`}
                  className={s.courseListItem}
                  onClick={event => checkAuth(event, course)}
                  key={course._id}
                >
                  <div className={s.bookIcon}>
                    <BookIcon color="black" />
                  </div>
                  <div>{course.name}</div>
                </Link>
              ))}
            </List>
          </>
        )}
        {!numberOfAttachedCourses && (
          <div className={s.noCoursesText}>
            <Typography variant="h6">
              <FormattedMessage {...messages.noCoursesText} />
            </Typography>
          </div>
        )}
        {isAttachedCoursesLoadingError && (
          <div className={s.noCoursesText}>
            <Typography variant="h6">
              <FormattedMessage {...messages.fetchedAttachedCoursesError} />
            </Typography>
          </div>
        )}
        <ModalCloseBtn
          data-test-id="leHXJACDGLIQ0YtQWH_r4"
          fitMobileLandscapeFullWidth
          onClick={onClose}
        />
      </CustomModal>
      <Modal
        open={isRestrictedAccessModalOpened}
        onClose={closeRestrictedAccessModal}
      >
        <RestrictedAccessModal onClose={closeRestrictedAccessModal} />
      </Modal>
    </>
  );
};

AttachedCoursesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  numberOfAttachedCourses: PropTypes.number.isRequired,
  lessonId: PropTypes.string.isRequired,  // eslint-disable-line
  auth: PropTypes.object,  // eslint-disable-line
};

export default compose(injectIntl, withStyles(s))(AttachedCoursesModal);
